import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { FormField, FormSelect } from '@/shared/ui';
import { SERVICE_REQUEST_REJECT_REASON_LABELS } from '@/entities/service-request';

import { useRejectServiceRequestForm } from '../model';

interface RejectServiceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceRequestId: number;
  trackIdnf?: string;
}

export const RejectServiceRequestModal = ({
  isOpen,
  onClose,
  serviceRequestId,
  trackIdnf
}: RejectServiceRequestModalProps) => {
  const { t } = useTranslation();

  const { form, onSubmit, isSubmitting } = useRejectServiceRequestForm({ serviceRequestId, onClose });
  const {
    control,
    register,
    formState: { errors }
  } = form;

  const reasonOptions = useMemo(
    () =>
      Object.entries(SERVICE_REQUEST_REJECT_REASON_LABELS).map(([value, label]) => ({
        value,
        label: t(`cmrlist.reject.reasons.${value}`, label)
      })),
    [t]
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {t('cmrlist.reject.onlinesrf.label')}
            {trackIdnf ? ` — ${trackIdnf}` : ''}
          </DialogTitle>
        </DialogHeader>

        <form id="reject-service-request-form" onSubmit={onSubmit} className="space-y-6 py-2">
          <FormSelect
            name="rejectReason"
            control={control}
            options={reasonOptions}
            label={t('cmrlist.reject.reason.label', 'Reason')}
            placeholder={t('cmrlist.reject.reason.placeholder', 'Select a reason')}
            error={errors.rejectReason?.message}
            className="w-full"
            required
          />

          <FormField
            label={t('cmrlist.reject.comment.label', 'Comment')}
            name="rejectComment"
            required
            error={errors.rejectComment?.message}>
            <Textarea
              id="rejectComment"
              {...register('rejectComment')}
              placeholder={t('cmrlist.reject.comment.placeholder', 'Explain why this request is being rejected')}
              rows={4}
            />
          </FormField>
        </form>

        <DialogFooter className="gap-3 sm:gap-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('common.closeDialog', 'Cancel')}
          </Button>
          <Button type="submit" form="reject-service-request-form" variant="destructive" disabled={isSubmitting}>
            {isSubmitting ? <Spinner className="mr-2 h-4 w-4" /> : <XCircle className="mr-2 h-4 w-4" />}
            {t('cmrlist.reject.submit', 'Reject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
